import { onBeforeUnmount, onMounted, ref, type Ref } from "vue";

/**
 * Reactive `window.matchMedia`.
 *
 * For the cases where a media query is not enough on its own: CSS can hide
 * an element at a breakpoint but it cannot move one somewhere else, so an
 * action bar that has to collapse into a menu on a phone needs the
 * breakpoint in script, not just in a stylesheet.
 *
 * Returns `false` until mounted, so a server-side or pre-mount read gets the
 * wide layout — the one that degrades gracefully if the query never
 * evaluates.
 */
export function useMediaQuery(query: string): Ref<boolean> {
  const matches = ref(false);
  let mql: MediaQueryList | null = null;

  const onChange = (e: MediaQueryListEvent) => {
    matches.value = e.matches;
  };

  onMounted(() => {
    // Guard the whole thing: jsdom and older Safari have historically had
    // partial matchMedia, and an action bar is not worth an exception that
    // breaks the surrounding component.
    if (typeof window === "undefined" || !window.matchMedia) return;
    mql = window.matchMedia(query);
    matches.value = mql.matches;
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
    } else {
      // Safari < 14 only has the deprecated listener API.
      (mql as MediaQueryList).addListener?.(onChange);
    }
  });

  onBeforeUnmount(() => {
    if (!mql) return;
    if (mql.removeEventListener) {
      mql.removeEventListener("change", onChange);
    } else {
      (mql as MediaQueryList).removeListener?.(onChange);
    }
  });

  return matches;
}

/** The phone breakpoint used across the app. */
export const NARROW_QUERY = "(max-width: 640px)";
